import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Observable, map, startWith } from 'rxjs';
import { ShipmentRouteFormGroup } from '@components/shipment-list/shipment-edit-dialog/shipment-edit-dialog.component';
import {
  ShipmentLocationViewModel,
  ShipmentRouteMapComponent,
  ShipmentRouteViewModel,
  StartOrArrivalLocationUpsertType,
} from '@components/shipment-route-list/shipment-route-map/shipment-route-map.component';

@Component({
  selector: 'app-shipment-route-edit',
  standalone: true,
  imports: [
    MatIconModule,
    CommonModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    ShipmentRouteMapComponent,
  ],
  templateUrl: './shipment-route-edit.component.html',
  styleUrl: './shipment-route-edit.component.scss',
})
export class ShipmentRouteEditComponent implements OnInit, OnChanges {
  /** 既存API互換のため入力は維持（画面では未使用） */
  @Input() routeList: ShipmentRouteViewModel[] = [];
  /** ルートフォーム */
  @Input({ required: true }) formGroup!: ShipmentRouteFormGroup;
  /** 位置情報一覧 */
  @Input() locationList: ShipmentLocationViewModel[] = [];

  /** 地図に表示する選択中ルート */
  public selectedRoute: ShipmentRouteViewModel | undefined;
  /** 出発地候補（入力で絞り込み） */
  public filteredStartLocationList: Observable<ShipmentLocationViewModel[]> =
    new Observable<ShipmentLocationViewModel[]>();
  /** 到着地候補（入力で絞り込み） */
  public filteredArrivalLocationList: Observable<ShipmentLocationViewModel[]> =
    new Observable<ShipmentLocationViewModel[]>();
  /** 出発地表示用（ID -> 地名） */
  public readonly displayStartLocationName = (value: string | null): string =>
    this.getStartLocationDisplayName(value);
  /** 到着地表示用（ID -> 地名） */
  public readonly displayArrivalLocationName = (value: string | null): string =>
    this.getArrivalLocationDisplayName(value);

  public ngOnInit(): void {
    this.applyLocationSelectionValidators();
    this.initStartLocationAutocomplete();
    this.initArrivalLocationAutocomplete();
    this.detectFormControlValueChanged();
    this.updateSelectedRoute();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['formGroup']) {
      this.applyLocationSelectionValidators();
      this.updateSelectedRoute();
    }

    if (changes['locationList']) {
      this.formGroup.controls.startLocationId.updateValueAndValidity({
        emitEvent: false,
      });
      this.formGroup.controls.arrivalLocationId.updateValueAndValidity({
        emitEvent: false,
      });
    }
  }

  /** 出発地を更新する */
  public upsertStartLocation(location: StartOrArrivalLocationUpsertType) {
    this.formGroup.controls.startLocationId.setValue(location.locationId);
  }

  /** 到着地を更新する */
  public upsertArrivalLocation(location: StartOrArrivalLocationUpsertType) {
    this.formGroup.controls.arrivalLocationId.setValue(location.locationId);
  }

  /** フォーム変更時に地図表示を追従させる */
  private detectFormControlValueChanged(): void {
    this.formGroup.valueChanges.subscribe(() => {
      this.updateSelectedRoute();
    });
  }

  /** 出発地候補を選択した時の処理 */
  public onStartLocationSelected(locationId: string): void {
    this.formGroup.controls.startLocationId.setValue(locationId);
  }

  /** 到着地候補を選択した時の処理 */
  public onArrivalLocationSelected(locationId: string): void {
    this.formGroup.controls.arrivalLocationId.setValue(locationId);
  }

  /** 現在のフォーム値から地図表示用ルートを作成する */
  private updateSelectedRoute(): void {
    const startLocationId = this.formGroup.get('startLocationId')?.value ?? '';
    const arrivalLocationId =
      this.formGroup.get('arrivalLocationId')?.value ?? '';

    const route = {
      startLocation: this.getLocationById(startLocationId),
      viaLocationList: [],
      arrivalLocation: this.getLocationById(arrivalLocationId),
    } as unknown as ShipmentRouteViewModel;

    this.selectedRoute = route;
  }

  private getLocationById(
    locationId: string,
  ): ShipmentLocationViewModel | undefined {
    return this.locationList.find(
      (location) => location.locationId === locationId,
    );
  }

  /** 出発地・到着地に候補選択バリデータを適用する */
  private applyLocationSelectionValidators(): void {
    const locationSelectionValidator = this.createLocationSelectionValidator();

    this.formGroup.controls.startLocationId.addValidators(
      locationSelectionValidator,
    );
    this.formGroup.controls.startLocationId.updateValueAndValidity({
      emitEvent: false,
    });

    this.formGroup.controls.arrivalLocationId.addValidators(
      locationSelectionValidator,
    );
    this.formGroup.controls.arrivalLocationId.updateValueAndValidity({
      emitEvent: false,
    });
  }

  /** locationId が候補一覧に存在するかを検証する */
  private createLocationSelectionValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as string | null;
      if (!value) {
        return null;
      }

      return this.getLocationById(value)
        ? null
        : { invalidLocationSelection: true };
    };
  }

  /** 出発地オートコンプリートを初期化する */
  private initStartLocationAutocomplete(): void {
    this.filteredStartLocationList = this.formGroup.controls.startLocationId.valueChanges.pipe(
      startWith(this.formGroup.controls.startLocationId.value),
      map((value) => this.filterStartLocation(value)),
    );
  }

  /** 到着地オートコンプリートを初期化する */
  private initArrivalLocationAutocomplete(): void {
    this.filteredArrivalLocationList = this.formGroup.controls.arrivalLocationId.valueChanges.pipe(
      startWith(this.formGroup.controls.arrivalLocationId.value),
      map((value) => this.filterArrivalLocation(value)),
    );
  }

  /** 出発地候補を入力キーワードで絞り込む */
  private filterStartLocation(value: string | null): ShipmentLocationViewModel[] {
    if (value && this.getLocationById(value)) {
      return this.locationList;
    }

    const keyword = this.getStartLocationDisplayName(value).trim().toLowerCase();
    if (!keyword) {
      return this.locationList;
    }

    return this.locationList.filter((location) =>
      location.locationName.toLowerCase().includes(keyword),
    );
  }

  /** 到着地候補を入力キーワードで絞り込む */
  private filterArrivalLocation(
    value: string | null,
  ): ShipmentLocationViewModel[] {
    if (value && this.getLocationById(value)) {
      return this.locationList;
    }

    const keyword = this.getArrivalLocationDisplayName(value).trim().toLowerCase();
    if (!keyword) {
      return this.locationList;
    }

    return this.locationList.filter((location) =>
      location.locationName.toLowerCase().includes(keyword),
    );
  }

  /** 出発地コントロール値を表示用文字列に変換する */
  public getStartLocationDisplayName(value: string | null): string {
    if (!value) {
      return '';
    }

    const location = this.getLocationById(value);
    return location ? location.locationName : value;
  }

  /** 到着地コントロール値を表示用文字列に変換する */
  public getArrivalLocationDisplayName(value: string | null): string {
    if (!value) {
      return '';
    }

    const location = this.getLocationById(value);
    return location ? location.locationName : value;
  }
}
